import { Capacitor } from "@capacitor/core";
import { FileSharer } from "@capgo/capacitor-file-sharer";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Empty, message, Space, Spin, Typography } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PdfViewer } from "shared/ui/pdf-viewer";
import styles from "./index.module.scss";

export const ViewDocument = ({
  documentId,
  localeKey,
  getDocumentPdfById,
  downloadDocumentById,
}: {
  documentId: string;
  localeKey: string;
  getDocumentPdfById: (id: string) => Promise<Blob>;
  downloadDocumentById: (id: string) => Promise<Blob>;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();

  const {
    data: file,
    isLoading: isLoadingFile,
    isError: isErrorFile,
    error: errorFile,
  } = useQuery({
    queryKey: ["file", documentId],
    queryFn: async () => {
      try {
        const response = await getDocumentPdfById(documentId!);
        return URL.createObjectURL(response);
      } catch (error) {
        console.log(error);
      }
    },
    enabled: !!documentId,
    retry: 5,
    retryDelay: 2500,
  });

  useEffect(() => {
    if (isErrorFile && errorFile) {
      messageApi.error(
        (errorFile as any)?.response?.data?.message ||
          t(`${localeKey}.download_error`),
      );
    }
  }, [isErrorFile, errorFile, messageApi, t, localeKey]);

  useEffect(() => {
    return () => {
      if (file) {
        queryClient.removeQueries({ queryKey: ["file", documentId] });
        URL.revokeObjectURL(file);
      }
    };
  }, [file, documentId, queryClient]);

  const downloadDocument = async () => {
    if (!documentId) return;

    try {
      const response = await downloadDocumentById(documentId);
      const fileType = response.type;
      const extension = fileType === "application/zip" ? "zip" : "pdf";
      const fileName = `document_${documentId}.${extension}`;

      if (Capacitor.isNativePlatform()) {
        const reader = new FileReader();
        reader.readAsDataURL(response);

        reader.onload = async () => {
          const base64Data = reader.result?.toString().split(",")[1] || "";
          try {
            await FileSharer.save({
              filename: fileName,
              base64Data: base64Data,
              contentType: fileType,
            });
            messageApi.success(t(`${localeKey}.document_downloaded`));
          } catch (error) {
            messageApi.error(t(`${localeKey}.download_error`));
          }
        };
      } else {
        const url = URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        messageApi.success(t(`${localeKey}.document_downloaded`));
      }
    } catch (error) {
      messageApi.error(t(`${localeKey}.download_error`));
    }
  };

  return (
    <Space
      orientation="vertical"
      size="middle"
      style={{ display: "flex", marginBottom: 24 }}
    >
      {contextHolder}
      <Typography.Title level={5}>
        {t(`${localeKey}.view_document`)}
      </Typography.Title>
      {isLoadingFile ? (
        <div className={styles.wrapperLoader}>
          <Spin />
        </div>
      ) : isErrorFile ? (
        <>
          <Empty
            description={
              <Typography.Text>
                {(errorFile as any)?.response?.data?.message ||
                  t(`${localeKey}.download_error`)}
              </Typography.Text>
            }
          />
          <Button
            type="link"
            block
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ["file", documentId] });
            }}
          >
            Попробовать снова
          </Button>
        </>
      ) : (
        <PdfViewer pdfFile={file} />
      )}

      <Button type="link" block onClick={downloadDocument}>
        {t(`${localeKey}.download_document`)}
      </Button>
    </Space>
  );
};
